// cookieConsent.ts — CORE-1142, CORE-1659
//
// Выбор по категориям cookie: одно значение на приложение, и все, кому оно
// нужно, читают именно его.
//
// ── Почему это заведено отдельным модулем ───────────────────────────────────
// До CORE-1142 согласие существовало наполовину: ключ `agape_cookie_consent` в
// сторадже был, компонент `CookieBanner` был — но баннер не рендерился нигде, а
// записанное значение не читал никто. То есть согласия у нас не спрашивали, и
// спросить было негде: состояние жило внутри самого баннера и никуда из него не
// выходило. Внешние счётчики без общего состояния пришлось бы гейтить второй
// копией той же логики, а две копии гейта — это гейт, который однажды
// разъедется.
//
// Значение хранится там же, где хранилось (`storage.getCookieConsent`). Прежний
// ответ «принять» / «отклонить» (до CORE-1659) читается как «всё разрешено» /
// «всё запрещено» и остаётся в сторадже как был, пока человек не выберет заново:
// по нему видно, что выбор по категориям ещё не делался.

import { storage } from '../utils/storage';
import {
  isLegacyCookieDecision,
  parseCookieChoice,
  serializeCookieChoice,
  type CookieChoice,
} from './cookieChoice';

/** `null` — спрашивали, человек ещё не ответил. `undefined` — ещё не читали. */
type State = CookieChoice | null | undefined;

let current: State = undefined;
let legacy = false;
let reading: Promise<CookieChoice | null> | null = null;
const listeners = new Set<(value: CookieChoice | null) => void>();

function emit(value: CookieChoice | null): void {
  for (const listener of listeners) {
    try {
      listener(value);
    } catch {
      // Подписчик — вспомогательный слой; его падение не должно отменять
      // согласие, которое человек только что дал.
    }
  }
}

/** Прочитать сохранённый выбор (один раз за жизнь приложения). */
export function loadCookieConsent(): Promise<CookieChoice | null> {
  if (current !== undefined) return Promise.resolve(current);
  if (!reading) {
    reading = storage
      .getCookieConsent()
      .catch(() => null)
      .then((raw) => {
        // Ответ мог прийти раньше чтения (баннер, сервер после входа) — он свежее.
        if (current !== undefined) return current;
        current = parseCookieChoice(raw);
        legacy = isLegacyCookieDecision(raw);
        emit(current);
        return current;
      });
  }
  return reading;
}

/** Синхронный снимок. `undefined` — ещё не прочитано. */
export function peekCookieConsent(): State {
  return current;
}

/**
 * Действует ли ещё ответ, данный прежнему баннеру («принять» / «отклонить» на
 * всё). На сервер такой ответ уходит под прежней версией текста: человек
 * отвечал на него, а не на выбор по категориям.
 */
export function isLegacyCookieConsent(): boolean {
  return legacy;
}

/** Записать выбор человека и разослать его подписчикам. */
export async function setCookieConsent(value: CookieChoice): Promise<void> {
  current = { analytics: value.analytics, marketing: value.marketing };
  legacy = false;
  emit(current);
  try {
    await storage.setCookieConsent(serializeCookieChoice(current));
  } catch {
    // Приватный режим: ответ не переживёт перезагрузку и его спросят снова.
    // Терять его в текущей сессии из-за этого незачем.
  }
}

/** Подписка на изменение выбора. Возвращает отписку. */
export function subscribeCookieConsent(listener: (value: CookieChoice | null) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
